// === ./src/frontend/components/Footer.jsx ===
import React, { useContext } from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Grid,
  Chip,
  Divider,
  alpha,
} from "@mui/material";
import { CloudDownload } from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import { TranslateContext } from "../../context/TranslateContext";

const Footer = ({ theme }) => {
  const history = useHistory();
  const isDark = theme.palette.mode === "dark";
  const { lang } = useContext(TranslateContext);

  const links = {
    [lang?.footerLegal || "Legal"]: [
      {
        label: lang?.footerPrivacy || "Privacy Policy",
        slug: "privacy-policy",
      },
      {
        label: lang?.footerTerms || "Terms of Service",
        slug: "terms-and-conditions",
      },
      { label: lang?.footerAbout || "About Us", slug: "about-us" },
    ],
    [lang?.footerAccount || "Account"]: [
      { label: lang?.signIn || "Sign In", slug: null },
      { label: lang?.footerRegister || "Register", slug: null },
      { label: lang?.footerDashboard || "Dashboard", slug: null },
    ],
  };

  const handleClick = (item) => {
    if (item.slug) {
      history.push(`/view/${item.slug}`);
      return;
    }
    if (item.label === (lang?.signIn || "Sign In")) history.push("/user/login");
    if (item.label === (lang?.footerRegister || "Register"))
      history.push("/user/register");
    if (item.label === (lang?.footerDashboard || "Dashboard"))
      history.push("/user");
  };

  return (
    <Box
      component="footer"
      sx={{
        py: { xs: 6, md: 8 },
        borderTop: `1px solid ${theme.palette.divider}`,
        bgcolor: isDark
          ? alpha(theme.palette.background.paper, 0.5)
          : alpha(theme.palette.background.paper, 0.8),
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} mb={5}>
          {/* Brand */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack direction="row" spacing={1.25} alignItems="center" mb={2}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.7)})`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
                }}
              >
                <CloudDownload sx={{ color: "#fff", fontSize: 18 }} />
              </Box>
              <Typography
                variant="subtitle1"
                fontWeight={800}
                sx={{ letterSpacing: "-0.02em" }}
              >
                TorrentCloud
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.7, maxWidth: 280, mb: 2 }}
            >
              {lang?.footerTagline ||
                "Cloud-powered torrent downloading. Fast, private, and accessible from anywhere. Your files, your cloud."}
            </Typography>
            <Stack direction="row" spacing={1}>
              {[
                {
                  label: lang?.footerBadgeSSL || "SSL",
                  color: theme.palette.success.main,
                },
                {
                  label: lang?.footerBadgeGDPR || "GDPR",
                  color: theme.palette.info.main,
                },
                {
                  label: lang?.footerBadgeUptime || "99.9% Uptime",
                  color: theme.palette.primary.main,
                },
              ].map((b) => (
                <Chip
                  key={b.label}
                  label={b.label}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.6rem",
                    fontWeight: 700,
                    bgcolor: alpha(b.color, 0.1),
                    color: b.color,
                    border: `1px solid ${alpha(b.color, 0.2)}`,
                  }}
                />
              ))}
            </Stack>
          </Grid>

          {/* Link columns */}
          {Object.entries(links).map(([section, items]) => (
            <Grid size={{ xs: 6, sm: 4, md: 2 }} key={section}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.disabled"
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  display: "block",
                  mb: 2,
                }}
              >
                {section}
              </Typography>
              <Stack spacing={1}>
                {items.map((item) => (
                  <Typography
                    key={item.label}
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      cursor: "pointer",
                      fontSize: "0.82rem",
                      transition: "color 0.2s",
                      "&:hover": { color: "primary.main" },
                    }}
                    onClick={() => handleClick(item)}
                  >
                    {item.label}
                  </Typography>
                ))}
              </Stack>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ mb: 3 }} />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
          spacing={1.5}
        >
          <Typography variant="caption" color="text.disabled">
            {lang?.footerCopyright ||
              `© ${new Date().getFullYear()} TorrentCloud. All rights reserved. Users are responsible for compliance with applicable laws.`}
          </Typography>
          <Stack direction="row" spacing={2}>
            {[
              {
                label: lang?.footerPrivacyShort || "Privacy",
                slug: "privacy-policy",
              },
              {
                label: lang?.footerTermsShort || "Terms",
                slug: "terms-and-conditions",
              },
              {
                label: lang?.footerAboutShort || "About",
                slug: "about-us",
              },
            ].map((l) => (
              <Typography
                key={l.label}
                variant="caption"
                color="text.secondary"
                sx={{
                  cursor: "pointer",
                  "&:hover": { color: "primary.main" },
                }}
                onClick={() => history.push(`/view/${l.slug}`)}
              >
                {l.label}
              </Typography>
            ))}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default Footer;
